import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { getFamilyClanRanking } from "../store/familyClan/action";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";

const numberFormat = new Intl.NumberFormat();

const PERIOD_OPTIONS = [
  { value: "week", label: "This week" },
  { value: "lastWeek", label: "Last week" },
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const DEFAULT_PERIOD = "all";

const FamilyClanRanking = ({ getFamilyClanRanking }) => {
  const { ranking, rankingLoading, rankingError } = useSelector(
    (state) => state.familyClan,
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [periodError, setPeriodError] = useState("");
  // Only an applied selection reaches the API, so editing the custom dates does
  // not refetch on every keystroke.
  const [appliedPeriod, setAppliedPeriod] = useState({
    period: DEFAULT_PERIOD,
  });

  useEffect(() => {
    getFamilyClanRanking(appliedPeriod);
  }, [getFamilyClanRanking, appliedPeriod]);

  const filteredRanking = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ranking;
    return ranking.filter(
      (entry) =>
        entry.familyClan?.name?.toLowerCase().includes(term) ||
        entry.familyClan?.clanId?.toLowerCase().includes(term) ||
        entry.captain?.name?.toLowerCase().includes(term) ||
        entry.captain?.username?.toLowerCase().includes(term),
    );
  }, [ranking, search]);

  useEffect(() => setPage(0), [search, rowsPerPage, period]);

  const visibleRanking =
    rowsPerPage === -1
      ? filteredRanking
      : filteredRanking.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        );
  const pageCount =
    rowsPerPage === -1 ? 1 : Math.ceil(filteredRanking.length / rowsPerPage);
  const firstEntry = filteredRanking.length
    ? rowsPerPage === -1
      ? 1
      : page * rowsPerPage + 1
    : 0;
  const lastEntry =
    rowsPerPage === -1
      ? filteredRanking.length
      : Math.min((page + 1) * rowsPerPage, filteredRanking.length);

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    setPeriod(value);
    setPeriodError("");

    if (value !== "custom") {
      setAppliedPeriod({ period: value });
    }
  };

  const handleCustomRangeSubmit = (event) => {
    event.preventDefault();

    if (!customStartDate || !customEndDate) {
      setPeriodError("Choose both a start date and an end date.");
      return;
    }

    if (customStartDate > customEndDate) {
      setPeriodError("The start date cannot be after the end date.");
      return;
    }

    setPeriodError("");
    setAppliedPeriod({
      startDate: customStartDate,
      endDate: customEndDate,
    });
  };

  return (
    <section
      className="family-clans-page"
      aria-labelledby="family-clan-ranking-title"
    >
      <div className="page-title">
        <div className="row align-items-end">
          <div className="col-12 col-md-7">
            <h3 id="family-clan-ranking-title" className="mb-2 text-white">
              Family Ranking
            </h3>
          </div>
          <div className="col-12 col-md-5">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-md-end"
            >
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Family Ranking
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card family-clans-card">
        <div className="card-header family-clans-toolbar">
          <div>
            <span className="family-clans-count">
              {numberFormat.format(ranking.length)}
            </span>
            <span className="family-clans-count-label"> ranked clans</span>
          </div>
          <div className="family-clans-toolbar-controls">
            <label className="family-clans-search">
              <span className="sr-only">Search family clans</span>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                value={search}
                placeholder="Search name, clan ID, or captain"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="family-clans-period">
              <span>Ranking Period</span>
              <select
                className="form-control"
                value={period}
                onChange={handlePeriodChange}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {period === "custom" && (
          <form
            className="family-clan-roster-filters"
            onSubmit={handleCustomRangeSubmit}
          >
            <label>
              <span>Start date</span>
              <input
                type="date"
                className="form-control"
                value={customStartDate}
                onChange={(event) => setCustomStartDate(event.target.value)}
                max={customEndDate || undefined}
              />
            </label>
            <label>
              <span>End date</span>
              <input
                type="date"
                className="form-control"
                value={customEndDate}
                onChange={(event) => setCustomEndDate(event.target.value)}
                min={customStartDate || undefined}
              />
            </label>
            <div className="family-clan-roster-filter-actions">
              <button type="submit" className="btn btn-sm btn-primary">
                Show ranking
              </button>
            </div>
          </form>
        )}

        {periodError && (
          <div className="family-clans-period-summary">
            <span className="text-danger">{periodError}</span>
          </div>
        )}

        <div className="card-body card-overflow p-0">
          <div className="table-responsive">
            <table className="table family-clans-table mb-0">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Clan</th>
                  <th>Captain</th>
                  <th>Level</th>
                  <th>Total Contribution</th>
                </tr>
              </thead>
              <tbody>
                {rankingLoading && (
                  <tr>
                    <td colSpan="5" className="family-clans-state">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading family clan ranking…
                    </td>
                  </tr>
                )}
                {!rankingLoading && rankingError && (
                  <tr>
                    <td colSpan="5" className="family-clans-state text-danger">
                      {rankingError}
                    </td>
                  </tr>
                )}
                {!rankingLoading &&
                  !rankingError &&
                  visibleRanking.map((entry) => (
                    <tr key={entry._id}>
                      <td>
                        <span className="family-clans-stat">
                          #{numberFormat.format(entry.rank || 0)}
                        </span>
                      </td>
                      <td>
                        <div className="family-clan-identity">
                          <img
                            src={entry.familyClan?.profilePicture || noImage}
                            onError={(event) => {
                              event.currentTarget.src = noImage;
                            }}
                            alt=""
                          />
                          <div>
                            <strong>
                              {entry.familyClan?.name || "Unnamed clan"}
                            </strong>
                            <div>
                              <code className="blue-primary">
                                {entry.familyClan?.clanId || "—"}
                              </code>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {entry.captain?.name ||
                          entry.captain?.username ||
                          "—"}
                      </td>
                      <td>
                        <div className="family-clan-level">
                          <img
                            src={entry.level?.image || noImage}
                            onError={(event) => {
                              event.currentTarget.src = noImage;
                            }}
                            alt=""
                          />
                          <span>{entry.level?.name || "Unranked"}</span>
                        </div>
                      </td>
                      <td>
                        {numberFormat.format(entry.totalContribution || 0)}
                      </td>
                    </tr>
                  ))}
                {!rankingLoading &&
                  !rankingError &&
                  !visibleRanking.length && (
                    <tr>
                      <td colSpan="5" className="family-clans-state">
                        No ranked family clans found.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>

        {!rankingLoading && !rankingError && filteredRanking.length > 0 && (
          <div className="card-footer family-clans-pagination">
            <span>
              Showing {firstEntry}–{lastEntry} of {filteredRanking.length}
            </span>
            <div className="family-clans-page-controls">
              <label>
                Rows{" "}
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(event) =>
                    setRowsPerPage(Number(event.target.value))
                  }
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={-1}>All</option>
                </select>
              </label>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0}
                onClick={() => setPage((value) => value - 1)}
                aria-label="Previous page"
              >
                &lsaquo;
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((value) => value + 1)}
                aria-label="Next page"
              >
                &rsaquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default connect(null, { getFamilyClanRanking })(FamilyClanRanking);
